import { CommonModule } from '@angular/common';
import { Component, OnInit, computed, effect, signal } from '@angular/core';
import type { EChartsOption } from 'echarts';
import { NGX_ECHARTS_CONFIG, NgxEchartsModule } from 'ngx-echarts';
import { SkeletonModule } from 'primeng/skeleton';
import { TooltipModule } from 'primeng/tooltip';

import { ProjectsData } from '../../../../models/workspace-overview.model';
import { WorkspaceOverviewStateService } from '../../../../services/workspace-overview-state.service';
import { WorkspaceOverviewService } from '../../../../services/workspace-overview.service';

@Component({
    selector: 'app-projects-widget',
    standalone: true,
    imports: [CommonModule, TooltipModule, NgxEchartsModule, SkeletonModule],
    providers: [
        { provide: NGX_ECHARTS_CONFIG, useValue: { echarts: () => import('echarts') } },
    ],
    templateUrl: './projects-widget.component.html',
    styleUrl: './projects-widget.component.scss',
})
export class ProjectsWidgetComponent implements OnInit {
    readonly data = signal<ProjectsData | null>(null);
    readonly loading = signal(false);
    readonly selectedIndex = signal<number>(-1);

    readonly total = computed(() =>
        (this.data()?.statuses ?? []).reduce((sum, s) => sum + s.count, 0)
    );

    private readonly hatchCanvas: HTMLCanvasElement = this.createHatchPattern();

    readonly chartOptions = computed(() => {
        const statuses = this.data()?.statuses ?? [];
        const sel = this.selectedIndex();
        const maxCount = statuses.length ? Math.max(...statuses.map(s => s.count)) : 0;
        const VISIBLE = 8;
        const endValue = Math.min(statuses.length - 1, VISIBLE - 1);
        const needsScroll = statuses.length > VISIBLE;

        return {
            animationDuration: 220,
            animationDurationUpdate: 220,
            animationEasing: 'linear' as const,
            animationEasingUpdate: 'linear' as const,
            grid: { top: 20, bottom: needsScroll ? 120 : 84, left: 50, right: 16 },
            tooltip: { show: true },
            dataZoom: needsScroll ? [
                {
                    type: 'inside',
                    orient: 'horizontal',
                    startValue: 0,
                    endValue,
                    zoomOnMouseWheel: false,
                    moveOnMouseWheel: true,
                    moveOnMouseMove: false,
                } as any,
                {
                    type: 'slider',
                    orient: 'horizontal',
                    startValue: 0,
                    endValue,
                    height: 20,
                    bottom: 10,
                    borderColor: '#EEEEF5',
                    fillerColor: '#E2E8FD',
                    handleStyle: { color: '#516FFB', borderColor: '#516FFB' },
                    moveHandleStyle: { color: '#516FFB' },
                    emphasis: { handleStyle: { color: '#516FFB' } },
                    showDetail: false,
                    showDataShadow: false,
                    brushSelect: false,
                } as any,
            ] : [],
            xAxis: {
                type: 'category',
                data: statuses.map(s => s.name),
                axisTick: { show: false },
                axisLabel: {
                    interval: 0,
                    rotate: 45,
                    fontSize: 12,
                    color: '#1C1C1C',
                    fontWeight: 500,
                    formatter: (value: string) =>
                        value === statuses[sel]?.name ? `{sel|${value}}` : `{normal|${value}}`,
                    rich: {
                        normal: {
                            color: '#1C1C1C',
                            fontSize: 12,
                            fontWeight: 500,
                            backgroundColor: 'transparent',
                            padding: [2, 12, 2, 12] as unknown as number,
                            borderRadius: 20,
                        },
                        sel: {
                            color: '#F97316',
                            fontSize: 12,
                            fontWeight: 700,
                            backgroundColor: '#FFE4E6',
                            padding: [2, 12, 2, 12] as unknown as number,
                            borderRadius: 20,
                        },
                    },
                },
            },
            yAxis: {
                type: 'value',
                minInterval: 1,
                splitLine: { lineStyle: { color: '#EEEEF5' } },
                axisLabel: { fontSize: 11, color: '#1C1C1C' },
            },
            series: [
                {
                    type: 'bar',
                    barMaxWidth: 56,
                    z: 1,
                    silent: true,
                    itemStyle: {
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        color: { image: this.hatchCanvas, repeat: 'repeat' } as any,
                        borderRadius: [12, 12, 12, 12],
                    },
                    data: statuses.map(() => maxCount),
                },
                {
                    type: 'bar',
                    barMaxWidth: 56,
                    barGap: '-100%',
                    z: 2,
                    markPoint: statuses.length ? {
                        symbol: 'circle',
                        symbolSize: sel >= 0 && statuses[sel] ? 8 : 0,
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        data: [{ coord: [sel >= 0 && statuses[sel] ? statuses[sel].name : statuses[0].name, sel >= 0 && statuses[sel] ? statuses[sel].count : 0] } as any],
                        itemStyle: { color: '#FFFFFF', borderColor: '#F76948', borderWidth: 2.5 },
                    } : { data: [] },
                    data: statuses.map((s, i) => ({
                        value: s.count,
                        itemStyle: {
                            color: i === sel ? '#FFA680' : '#516FFB',
                            opacity: i === sel ? 1 : 0.9,
                            borderRadius: [12, 12, 12, 12],
                        },
                    })),
                },
            ],
        } as EChartsOption;
    });

    constructor(
        private readonly service: WorkspaceOverviewService,
        private readonly state: WorkspaceOverviewStateService,
    ) {
        effect(() => {
            void this.state.viewMode();
            void this.state.period();
            void this.state.effectiveRange();
            this.fetch();
        });
    }

    ngOnInit(): void { /* initial fetch triggered by the effect above */ }

    onChartClick(event: { dataIndex?: number }): void {
        const idx = event?.dataIndex;
        if (idx !== undefined && idx >= 0) {
            this.selectedIndex.set(idx === this.selectedIndex() ? -1 : idx);
        }
    }

    private createHatchPattern(): HTMLCanvasElement {
        const size = 8;
        const canvas = document.createElement('canvas');
        canvas.width = size;
        canvas.height = size;
        const ctx = canvas.getContext('2d')!;
        ctx.fillStyle = '#F9FAFB';
        ctx.fillRect(0, 0, size, size);
        ctx.strokeStyle = '#E4E4EE';
        ctx.lineWidth = 1.5;
        ctx.beginPath(); ctx.moveTo(0, size); ctx.lineTo(size, 0); ctx.stroke();
        ctx.beginPath(); ctx.moveTo(-size, size); ctx.lineTo(0, 0); ctx.stroke();
        ctx.beginPath(); ctx.moveTo(size, size); ctx.lineTo(size * 2, 0); ctx.stroke();
        return canvas;
    }

    private fetch(): void {
        this.loading.set(true);
        this.service.getProjects({ mode: this.state.viewMode() }).subscribe({
            next: data => { this.data.set(data); this.loading.set(false); },
            error: () => this.loading.set(false),
        });
    }
}
